import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  ReactNode,
} from "react";
import { cartApi, type ServerCartItem } from "@/hooks/use-cart-mutations";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import type { ImportCartRejectedItem } from "@/types/api";
import { importDraft } from "@/lib/order-import/draft";

export interface CartItem {
  id: string;
  name: string;
  article: string;
  image: string;
  priceRetail: number;
  priceWholesale: number;
  discount: number;
  quantity: number;
  unit: string;
  minOrder: number;
  quantityStep?: number;
  stock: number;
}

/** "loading" until the first GET /cart settles; "error" only when that GET failed. */
export type CartStatus = "loading" | "ready" | "error";

interface CartContextType {
  items: CartItem[];
  status: CartStatus;
  /** Re-runs the initial load after an error. */
  retry: () => void;
  /** Resolves true once the server accepted the item; false after it was rolled back. */
  addItem: (item: CartItem) => Promise<boolean>;
  /**
   * Adds many lines in one request (order import, repeat order) and replaces the cart with
   * the server's.
   * Resolves with the lines the server refused; rejects when the request itself failed.
   */
  importItems: (
    items: { productId: string; quantity: number }[],
  ) => Promise<{ rejected: ImportCartRejectedItem[] }>;
  removeItem: (id: string) => void;
  restoreItems: (entries: { item: CartItem; index: number }[]) => void;
  updateQuantity: (id: string, quantity: number) => void;
  /** Sends any debounced quantity change now; await before ordering. */
  flushQuantityUpdates: () => Promise<void>;
  clearCart: () => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
  syncWithServer: () => Promise<void>;
  resetCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const QUANTITY_DEBOUNCE_MS = 400;

function toCartItem(s: ServerCartItem): CartItem {
  return {
    id: s.productId,
    name: s.name,
    article: s.article,
    image: s.image,
    priceRetail: s.priceRetail,
    priceWholesale: s.priceWholesale,
    discount: s.discount,
    quantity: s.quantity,
    unit: s.unit,
    minOrder: s.minOrder,
    quantityStep: s.quantityStep ?? 1,
    stock: s.stock,
  };
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [status, setStatus] = useState<CartStatus>("loading");
  const { isAuthenticated, isLoading } = useAuth();

  /** Takes the server's cart as the truth (after a GET or a batch import). */
  const applyServerCart = useCallback((serverItems: ServerCartItem[]) => {
    setItems(serverItems.map(toCartItem));
    setStatus("ready");
  }, []);

  // A catalog export can change ordering metadata while the shopper is editing.
  // Keep newer optimistic quantities; refresh only the rules from mutation responses.
  const applyServerRules = useCallback((serverItems: ServerCartItem[]) => {
    const rules = new Map(serverItems.map((item) => [item.productId, item]));
    setItems((previous) =>
      previous.map((item) => {
        const current = rules.get(item.id);
        return current
          ? {
              ...item,
              unit: current.unit,
              minOrder: current.minOrder,
              quantityStep: current.quantityStep ?? 1,
            }
          : item;
      }),
    );
  }, []);

  const fetchAndSetCart = useCallback(async () => {
    applyServerCart((await cartApi.getCart()).items);
  }, [applyServerCart]);

  // Optimistic edits are only kept if the server agrees; otherwise the server's cart wins,
  // since that is what checkout orders from.
  const resyncAfterFailure = useCallback(
    (message: string) => {
      toast.error(message);
      fetchAndSetCart().catch(() => {});
    },
    [fetchAndSetCart],
  );

  const addItem = useCallback(
    (item: CartItem) => {
      setItems((prev) => {
        const existingItem = prev.find((i) => i.id === item.id);
        if (existingItem) {
          return prev.map((i) =>
            i.id === item.id ? { ...i, quantity: i.quantity + item.quantity } : i,
          );
        }
        return [...prev, item];
      });
      return cartApi.addItem(item.id, item.quantity).then(
        (response) => {
          applyServerRules(response.items);
          return true;
        },
        () => {
          resyncAfterFailure("Не удалось добавить товар");
          return false;
        },
      );
    },
    [resyncAfterFailure, applyServerRules],
  );

  // Deletions are undoable (see restoreItems), so the DELETE has to be waitable: a fast
  // undo on a slow link would otherwise POST the item back before the DELETE lands, and
  // the server would drop it again behind the user's back.
  const pendingRemovals = useRef(new Map<string, Promise<unknown>>());

  // Stepper clicks arrive one per tap; only the last value per item within the window is
  // sent, so a burst of taps can't land on the server out of order.
  const quantityTimers = useRef(new Map<string, { timer: number; send: () => Promise<void> }>());

  const removeItem = useCallback((id: string) => {
    const pending = quantityTimers.current.get(id);
    if (pending) {
      window.clearTimeout(pending.timer);
      quantityTimers.current.delete(id);
    }
    setItems((prev) => prev.filter((item) => item.id !== id));
    const request = cartApi
      .removeItem(id)
      .catch(() => toast.error("Не удалось удалить товар"))
      .finally(() => {
        if (pendingRemovals.current.get(id) === request) pendingRemovals.current.delete(id);
      });
    pendingRemovals.current.set(id, request);
  }, []);

  // Puts removed items back where they were. Indices come from the pre-deletion array, so
  // replaying them in ascending order reproduces the original row order exactly.
  const restoreItems = useCallback(
    (entries: { item: CartItem; index: number }[]) => {
      const ordered = [...entries].sort((a, b) => a.index - b.index);

      setItems((prev) => {
        const next = [...prev];
        for (const { item, index } of ordered) {
          if (next.some((i) => i.id === item.id)) continue;
          next.splice(Math.min(index, next.length), 0, item);
        }
        return next;
      });

      Promise.all(
        ordered.map(async ({ item }) => {
          await pendingRemovals.current.get(item.id);
          return cartApi.addItem(item.id, item.quantity);
        }),
      ).catch(() => {
        toast.error("Не удалось восстановить товар");
        fetchAndSetCart().catch(() => {});
      });
    },
    [fetchAndSetCart],
  );

  const updateQuantity = useCallback(
    (id: string, quantity: number) => {
      setItems((prev) => prev.map((item) => (item.id === id ? { ...item, quantity } : item)));

      const previous = quantityTimers.current.get(id);
      if (previous) window.clearTimeout(previous.timer);
      const send = () => {
        quantityTimers.current.delete(id);
        return cartApi
          .updateItem(id, quantity)
          .then((response) => applyServerRules(response.items))
          .catch(() => resyncAfterFailure("Не удалось обновить количество"));
      };
      quantityTimers.current.set(id, {
        timer: window.setTimeout(send, QUANTITY_DEBOUNCE_MS),
        send,
      });
    },
    [resyncAfterFailure, applyServerRules],
  );

  const flushQuantityUpdates = useCallback(async () => {
    const pending = [...quantityTimers.current.values()];
    pending.forEach(({ timer }) => window.clearTimeout(timer));
    await Promise.all(pending.map(({ send }) => send()));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    cartApi.clearCart().catch(() => {});
  }, []);

  const getTotalItems = useCallback(
    () => items.reduce((total, item) => total + item.quantity, 0),
    [items],
  );

  const getTotalPrice = useCallback(
    () =>
      items.reduce((total, item) => {
        const price = item.discount
          ? Math.round(item.priceWholesale * (1 - item.discount / 100))
          : item.priceWholesale;
        return total + price * item.quantity;
      }, 0),
    [items],
  );

  const syncWithServer = fetchAndSetCart;

  const resetCart = useCallback(() => {
    setItems([]);
    setStatus("ready");
  }, []);

  const loadCart = useCallback(() => {
    setStatus("loading");
    fetchAndSetCart().catch(() => setStatus("error"));
  }, [fetchAndSetCart]);

  const importItems = useCallback(
    async (newItems: { productId: string; quantity: number }[]) => {
      // The response replaces the whole cart, so any write still in flight (a debounced
      // stepper change, a DELETE) has to land first or it would undo part of the import.
      await flushQuantityUpdates();
      await Promise.all(pendingRemovals.current.values());
      try {
        const res = await cartApi.importItems(newItems);
        applyServerCart(res.items);
        return { rejected: res.rejected };
      } catch (error) {
        fetchAndSetCart().catch(() => {});
        throw error;
      }
    },
    [applyServerCart, fetchAndSetCart, flushQuantityUpdates],
  );

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) {
      loadCart();
    } else {
      resetCart();
      // The unfinished import list is the signed-out dealer's too; the next one to sign
      // in in this tab mustn't get it.
      importDraft.set(null);
    }
  }, [isAuthenticated, isLoading, loadCart, resetCart]);

  // Memoised: an unstable value re-renders every consumer (i.e. every product card
  // in the grid) on each cart mutation, defeating the memo() on the cards.
  const value = useMemo(
    () => ({
      items,
      status,
      retry: loadCart,
      addItem,
      importItems,
      removeItem,
      restoreItems,
      updateQuantity,
      flushQuantityUpdates,
      clearCart,
      getTotalItems,
      getTotalPrice,
      syncWithServer,
      resetCart,
    }),
    [
      items,
      status,
      loadCart,
      addItem,
      importItems,
      removeItem,
      restoreItems,
      updateQuantity,
      flushQuantityUpdates,
      clearCart,
      getTotalItems,
      getTotalPrice,
      syncWithServer,
      resetCart,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
